import React, { useEffect, useState } from 'react';
import { Button, Card, Form, InputGroup, Modal, Spinner } from 'react-bootstrap';
import { ReactComponent as PlusIcon } from '../../images/plus.svg';
import axiosInstance from '../../api/api';
import { API_ROUTES } from '../../config/api-routes';
import { toast } from 'react-toastify';
import '../Preview/Preview.css';

type ProfileProfessionalLinksCardProps = {
  userData: any;
  setUserData?: React.Dispatch<React.SetStateAction<any>>;
};

const ProfileProfessionalLinksCard: React.FC<ProfileProfessionalLinksCardProps> = ({
  userData,
  setUserData,
}) => {
  const [showLinksModal, setShowLinksModal] = useState(false);
  const [linksSaveLoading, setLinksSaveLoading] = useState(false);
  const [professionalLinks, setProfessionalLinks] = useState<string[]>(['']);

  useEffect(() => {
    const normalizedLinks: string[] = [];
    if (userData?.linkedin) {
      normalizedLinks.push(String(userData.linkedin));
    }

    const rawOtherSocial = userData?.otherSocial;
    if (Array.isArray(rawOtherSocial)) {
      rawOtherSocial.forEach((entry) => {
        if (entry) normalizedLinks.push(String(entry));
      });
    } else if (typeof rawOtherSocial === 'string' && rawOtherSocial.trim()) {
      try {
        const parsed = JSON.parse(rawOtherSocial);
        if (Array.isArray(parsed)) {
          parsed.forEach((entry) => {
            if (entry) normalizedLinks.push(String(entry));
          });
        } else {
          normalizedLinks.push(rawOtherSocial);
        }
      } catch {
        normalizedLinks.push(rawOtherSocial);
      }
    }

    const deduped = normalizedLinks.filter(
      (link, idx, arr) => link.trim() && arr.indexOf(link) === idx
    );
    setProfessionalLinks(deduped.length > 0 ? deduped.slice(0, 10) : ['']);
  }, [userData?.linkedin, userData?.otherSocial]);

  const handleSaveLinks = async () => {
    setLinksSaveLoading(true);
    try {
      const cleaned = professionalLinks
        .map((link) => link.trim())
        .filter((link, idx, arr) => Boolean(link) && arr.indexOf(link) === idx)
        .slice(0, 10);
      const [linkedin, ...otherLinks] = cleaned;

      const response = await axiosInstance.put(API_ROUTES.USER.UPDATE_USER, {
        linkedin: linkedin || '',
        otherSocial: JSON.stringify(otherLinks),
      });

      if (!response || response.status !== 200) {
        throw new Error(`Failed to update links: ${response?.statusText}`);
      }

      setUserData?.((prevState: any) => ({
        ...prevState,
        linkedin: linkedin || '',
        otherSocial: JSON.stringify(otherLinks),
      }));
      setShowLinksModal(false);
      toast.success('Professional links updated successfully!');
    } catch (error) {
      console.error('Failed to update links:', error);
      toast.error('Failed to update links');
    } finally {
      setLinksSaveLoading(false);
    }
  };

  const handleProfessionalLinkChange = (index: number, value: string) => {
    setProfessionalLinks((prev) => prev.map((link, idx) => (idx === index ? value : link)));
  };

  const addProfessionalLinkField = () => {
    setProfessionalLinks((prev) => (prev.length >= 10 ? prev : [...prev, '']));
  };

  const removeProfessionalLinkField = (index: number) => {
    setProfessionalLinks((prev) => {
      const next = prev.filter((_, idx) => idx !== index);
      return next.length > 0 ? next : [''];
    });
  };

  const visibleProfessionalLinks = professionalLinks.map((link) => link.trim()).filter(Boolean);

  const getProfessionalLinkLabel = (rawLink: string) => {
    try {
      const normalized = rawLink.startsWith('http') ? rawLink : `https://${rawLink}`;
      const parsed = new URL(normalized);
      const host = parsed.hostname.replace(/^www\./, '');
      const path = parsed.pathname.replace(/\/$/, '');
      return path ? `${host}${path}` : host;
    } catch {
      return rawLink;
    }
  };

  return (
    <>
      <Card
        id="professional-links-card"
        className="references-card-modern profile-links-card-modern mb-0"
        data-profile-focus="professional-links"
      >
        <Card.Header className="preview-cards-ref-header references-header">
          <span className="references-title">Professional Links</span>
          <div className="references-header-actions">
            <button
              type="button"
              title="Edit Professional Links"
              className="user-info-edit-button"
              style={{ position: 'relative' }}
              data-profile-focus="professional-links-add"
              onClick={() => setShowLinksModal(true)}
            >
              <PlusIcon style={{ width: '20px', height: '20px' }} />
            </button>
          </div>
        </Card.Header>
        <Card.Body className="references-body profile-links-body">
          {visibleProfessionalLinks.length ? (
            <div className="profile-links-list">
              {visibleProfessionalLinks.map((link, index) => (
                <a
                  key={`display-link-${index}`}
                  href={link.startsWith('http') ? link : `https://${link}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="profile-link-row"
                  title={link}
                >
                  <span>{getProfessionalLinkLabel(link)}</span>
                  <span className="profile-link-row-arrow" aria-hidden="true">
                    ↗
                  </span>
                </a>
              ))}
            </div>
          ) : (
            <p className="profile-links-empty mb-0">
              Add your LinkedIn, GitHub, portfolio, or certifications. Up to 10 links supported.
            </p>
          )}
        </Card.Body>
      </Card>

      <Modal
        className="ss-app-modal"
        show={showLinksModal}
        onHide={() => setShowLinksModal(false)}
        centered
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>Professional Links</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">
            Add up to 10 professional links (LinkedIn, GitHub, portfolio, certifications, etc.).
          </p>
          {professionalLinks.map((link, index) => (
            <InputGroup size="sm" className="mb-2" key={`pro-link-${index}`}>
              <Form.Control
                type="url"
                placeholder={
                  index === 0
                    ? 'https://www.linkedin.com/in/your-name'
                    : 'https://github.com/username or portfolio link'
                }
                value={link}
                onChange={(e) => handleProfessionalLinkChange(index, e.target.value)}
              />
              <Button
                variant="outline-danger"
                onClick={() => removeProfessionalLinkField(index)}
                disabled={professionalLinks.length === 1}
                title="Remove link"
              >
                Remove
              </Button>
            </InputGroup>
          ))}
          <Button
            variant="outline-success"
            onClick={addProfessionalLinkField}
            disabled={professionalLinks.length >= 10}
          >
            Add another link ({professionalLinks.length}/10)
          </Button>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowLinksModal(false)}
            disabled={linksSaveLoading}
          >
            Cancel
          </Button>
          <Button variant="success" onClick={handleSaveLinks} disabled={linksSaveLoading}>
            {linksSaveLoading ? <Spinner animation="border" size="sm" /> : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ProfileProfessionalLinksCard;
